import { z } from 'zod';
import { timestampSchema, ulidSchema } from './common.js';

/**
 * The analytics contract, shared by everything that produces or reads an event.
 *
 * Three tiers store this data and each one is here for a reason the other two cannot cover:
 *
 * - **Analytics Engine** takes every event. It is the exploratory tier: unlimited cardinality,
 *   no schema to migrate, and it forgets after three months.
 * - **D1** takes the events marked durable below, and the nightly rollups. It is the tier that
 *   has to still be true next year, and the only one that can join to `games` and `users`.
 * - **Cloudflare Web Analytics** takes anonymous web traffic, and no code here touches it.
 *
 * The allowlists are the whole point of this file. A dimension a client can invent is a
 * dimension a hostile client can use to blow up cardinality, so a name that is not in one of
 * these lists is dropped at ingest rather than stored.
 */

/* ------------------------------------------------------------------ app events */

export const ANALYTICS_EVENTS = [
  /** A screen was opened. `route` is the route template, never a resolved URL. */
  'page_view',
  /** First event of a visit, once per session. */
  'session_start',
  /** A search was run. The number of results is the value; the query text is never sent. */
  'search',
  'signin_started',
  'signin_completed',
  /** Sent to the checkout. The membership webhook is what says whether it completed. */
  'checkout_started',
  'install_started',
  'install_completed',
  'install_failed',
  'update_completed',
  'game_launch',
  /** A game exited. `value` is the session length in seconds. */
  'game_session_end',
  /** A launch was refused, with the reason in `detail`. The signal nobody can see today. */
  'launch_refused',
] as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[number];
export const analyticsEventNameSchema = z.enum(ANALYTICS_EVENTS);

/**
 * Events that are also written to D1, and so outlive Analytics Engine's three month window.
 *
 * The test is volume against value. A page view is high volume and interesting in aggregate, so
 * Analytics Engine keeps it and the nightly rollup keeps the shape of it forever. A finished
 * play session is a few thousand rows a day at most and is worth having exactly, per user, in
 * five years, so it goes in a table.
 */
export const DURABLE_EVENTS = [
  'game_session_end',
  'game_launch',
  'install_completed',
  'install_failed',
  'launch_refused',
  'signin_completed',
  'checkout_started',
] as const satisfies readonly AnalyticsEventName[];

const DURABLE = new Set<string>(DURABLE_EVENTS);

export function isDurableEvent(name: string): boolean {
  return DURABLE.has(name);
}

/**
 * Every route the client may report, as a template.
 *
 * Templates rather than URLs: `/games/$slug` and never `/games/some-game`, so the dimension has
 * thirty-odd values instead of one per game, and so an id in a path (`/invite/$id`) is never
 * stored next to the person who opened it.
 *
 * A route missing from this list is recorded as `other` rather than refused. Adding a screen
 * should not be able to break ingest; it should only mean the screen is uncounted until its
 * template is added here.
 */
export const ANALYTICS_ROUTES = [
  '/',
  '/home',
  '/games',
  '/games/$slug',
  '/library',
  '/modding',
  '/search',
  '/downloads',
  '/download',
  '/notifications',
  '/settings',
  '/account',
  '/crew',
  '/creator',
  '/studio',
  '/join',
  '/signin',
  '/invite/$id',
  '/privacy',
  '/terms',
  '/auth/handoff',
  '/admin',
  '/admin/games',
  '/admin/games/$id/edit',
  '/admin/games/$id/builds',
  '/admin/team',
  '/admin/members',
  '/admin/roles',
  '/admin/creators',
  '/admin/storage',
  '/admin/webhooks',
  '/admin/api-keys',
  '/admin/audit',
  '/admin/insights',
] as const;

const ROUTES = new Set<string>(ANALYTICS_ROUTES);

/** The route as it will be stored: a known template, or `other`. */
export function normaliseRoute(route: string | undefined): string | undefined {
  if (route === undefined) return undefined;
  return ROUTES.has(route) ? route : 'other';
}

/**
 * The most events one request may carry.
 *
 * Twenty rather than something larger because Analytics Engine allows 250 data points per
 * Worker invocation, and a batch of twenty leaves room for the play telemetry path to share a
 * Worker without either of them having to think about it.
 */
export const ANALYTICS_BATCH_MAX = 20;

export const analyticsEventSchema = z.object({
  t: analyticsEventNameSchema,
  /** The client's clock. Used only to order a batch; the stored time is the server's. */
  ts: timestampSchema,
  route: z.string().max(80).optional(),
  gameId: ulidSchema.optional(),
  /** Whatever the event measures: seconds played, bytes installed, results found. */
  value: z.number().finite().optional(),
  /** One short label: an error code, a refusal reason, a referrer host. Never free text. */
  detail: z
    .string()
    .max(64)
    .regex(/^[\w.:-]+$/, 'a short label, not free text')
    .optional(),
});
export type AnalyticsEvent = z.infer<typeof analyticsEventSchema>;

export const analyticsBatchSchema = z.object({
  events: z.array(analyticsEventSchema).min(1).max(ANALYTICS_BATCH_MAX),
});
export type AnalyticsBatch = z.infer<typeof analyticsBatchSchema>;

/* ----------------------------------------------------------------- play events */

/**
 * What a game reports from inside itself.
 *
 * Deliberately generic. A game that cannot start while an update is pending, which is every
 * game here because they are all multiplayer, turns "add an event type" into "everyone
 * downloads a patch before they can play". So the shape is a name plus a number, and a new
 * question is usually a new *value* of `name` rather than a new event, needing no new build.
 */
export const PLAY_EVENTS = [
  /** A level, act or match began. */
  'level_start',
  /** Progress past a named point. The backbone of the "where do they get stuck" funnel. */
  'checkpoint',
  /** Died, failed or was defeated. `name` is the cause, `area` is where. */
  'death',
  'level_complete',
  /** Left mid level. `value` is percent complete where the game can say. */
  'quit',
  /** A generic named milestone, for anything the five above do not describe. */
  'objective',
] as const;

export type PlayEventName = (typeof PLAY_EVENTS)[number];
export const playEventNameSchema = z.enum(PLAY_EVENTS);

/**
 * A label a game chooses: a level, an area, a cause of death.
 *
 * Constrained rather than free text, and short, because these become dimensions. The character
 * class also means a label can never carry a player name, a chat line or a file path, which is
 * the kind of thing that ends up in a string field by accident.
 */
export const playLabelSchema = z
  .string()
  .min(1)
  .max(48)
  .regex(/^[a-z0-9][a-z0-9._-]*$/, 'lowercase label, like act1.bridge');

export const playEventSchema = z.object({
  t: playEventNameSchema,
  /** What it happened to: `act1.bridge`, `boss.warden`, `fell`. */
  name: playLabelSchema,
  /** Where it happened. Coarse on purpose: a level or region, never coordinates. */
  area: playLabelSchema.optional(),
  /** Seconds elapsed inside the play session when it happened. */
  at: z.number().finite().nonnegative().max(86_400),
  /** Ordinal, for funnels that need the steps in order. */
  index: z.number().int().nonnegative().max(10_000).optional(),
  /** Whatever the event measures: seconds taken, percent complete, attempts. */
  value: z.number().finite().optional(),
});
export type PlayEvent = z.infer<typeof playEventSchema>;

/**
 * Larger than the app batch: a game buffers locally and flushes every thirty seconds or so, and
 * a round trip from inside a running game is worth spending less often.
 */
export const PLAY_BATCH_MAX = 50;

export const playBatchSchema = z.object({
  events: z.array(playEventSchema).min(1).max(PLAY_BATCH_MAX),
});
export type PlayBatch = z.infer<typeof playBatchSchema>;

/**
 * How long a play token is good for.
 *
 * Long enough for a session nobody would call short, short enough that a token lifted off a
 * machine is worthless by the next day. It can only append events for one game and one session,
 * so what it protects is the integrity of a funnel rather than an account.
 */
export const PLAY_TOKEN_TTL_S = 12 * 60 * 60;

export const playSessionRequestSchema = z.object({
  gameId: ulidSchema,
  buildId: ulidSchema.optional(),
});

export const playSessionSchema = z.object({
  token: z.string(),
  playSessionId: ulidSchema,
  endpoint: z.string(),
  expiresAt: timestampSchema,
});
export type PlaySession = z.infer<typeof playSessionSchema>;

/** Claims in a play token. Everything a play event is attributed to comes from here. */
export const playTokenClaimsSchema = z.object({
  typ: z.literal('play'),
  sub: ulidSchema,
  gid: ulidSchema,
  psid: ulidSchema,
  iat: z.number().int(),
  exp: z.number().int(),
});
export type PlayTokenClaims = z.infer<typeof playTokenClaimsSchema>;
